import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Badge, Button, Row, Col } from "react-bootstrap";
import { FaMapMarkerAlt, FaPhone, FaWhatsapp, FaClock, FaIdCard, FaCalendarAlt, FaStar, FaExclamationTriangle, FaArrowLeft } from "react-icons/fa";
import { FaClipboardList, FaGlobe, FaHandsHelping, FaUsers, FaShieldAlt, FaCogs } from "react-icons/fa";
import Navbar from '../../Pages/Navbar/Navbar';
import defaultImage from "../../Pages/Images/young-beautiful-teacher-toddlers-playing-260nw-1690362274.webp";
import "./individualTraining.css";
import BaseUrl from '../../ApiServices/BaseUrl';

const IndividualTraining = () => {
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const trainingId = searchParams.get('id');

  useEffect(() => {
    if (trainingId) {
      fetchTrainingDetails();
    }
  }, [trainingId]);

  const fetchTrainingDetails = async () => {
    try {
      const response = await fetch(`${BaseUrl}/trainings/${trainingId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch training details');
      }
      const data = await response.json();
      setTraining(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/training');
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container mt-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading training details...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar />
        <div className="container mt-4">
          <div className="alert alert-danger text-center mt-5" role="alert">
            Error: {error}
          </div>
          <Button variant="primary" onClick={handleBack} className="mt-3">
            <FaArrowLeft className="me-2" />
            Back to Trainings
          </Button>
        </div>
      </>
    );
  }

  if (!training) {
    return (
      <>
        <Navbar />
        <div className="container mt-4">
          <div className="alert alert-warning text-center mt-5" role="alert">
            Training not found
          </div>
          <Button variant="primary" onClick={handleBack} className="mt-3">
            <FaArrowLeft className="me-2" />
            Back to Trainings
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        {/* Back Button */}
        <Button 
          variant="outline-primary" 
          onClick={handleBack} 
          className="mb-3"
        >
          <FaArrowLeft className="me-2" />
          Back to Trainings
        </Button>

        {/* Main Training Card */}
        <Card className="clutter-card shadow-sm border-0 rounded-3 overflow-hidden mb-4">
          <div className="clutter-banner">
            <img 
              src={training.back_profile || defaultImage} 
              alt={`${training.training_title} banner`} 
              className="banner-img" 
            />
            <div className="clutter-logo">
              <img 
                src={training.profile_image || defaultImage} 
                alt={`${training.training_title} logo`} 
              />
            </div>
          </div>

          <Card.Body className="clutter-body">
            <div className="d-flex align-items-center mb-3">
              <div>
                <h4 className="mb-1 text-primary fw-bold">{training.training_title}</h4>
                <Badge bg="warning" text="dark" className="px-2 py-1">
                  <FaStar className="me-1" />
                  {training.rating} Rating
                </Badge>
              </div>
              <div className="ms-auto d-flex align-items-center text-danger fw-semibold">
                <FaMapMarkerAlt className="me-1" />
                <span>{training.location}</span>
              </div>
            </div>

            <p className="text-muted">
              {training.description}
            </p>

            <h6 className="fw-bold">Our Services</h6>
            <p className="fw-semibold text-primary">"{training.our_services}"</p>

            {/* Additional training details can be added here based on your database structure */}
            <div className="training-details mt-4">
              <h6 className="fw-bold">Training Overview</h6>
              <p className="text-muted">
                This training program is designed to provide comprehensive skills and knowledge 
                to domestic workers. Our certified trainers ensure quality education and practical 
                experience.
              </p>
            </div>
          </Card.Body>
        </Card>

        {/* ================== Services Section ================== */}
        <section className="services-section my-5">
          <h4 className="fw-bold text-primary text-center mb-4">Services</h4>
          <Row className="g-4 justify-content-center">
            {/* Online Training Card */}
            <Col md={6} lg={4}>
              <Card className="service-card h-100 shadow-sm border-0 rounded-3">
                <Card.Body className="text-center p-4">
                  <div className="service-icon bg-primary text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                    <FaUsers size={24} />
                  </div>
                  <h5 className="fw-semibold text-primary">Online Training</h5>
                  <p className="text-muted mb-2">Flexible learning from anywhere</p>
                  <div className="price-tag bg-light rounded-pill px-3 py-1 mb-3">
                    <span className="fw-bold text-primary">From HK$ 350</span>
                  </div>
                  <Button variant="outline-primary" className="px-4">
                    MORE DETAILS
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            {/* Our Services Card */}
            <Col md={6} lg={4}>
              <Card className="service-card h-100 shadow-sm border-0 rounded-3">
                <Card.Body className="text-center p-4">
                  <div className="service-icon bg-success text-white rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                    <FaHandsHelping size={24} />
                  </div>
                  <h5 className="fw-semibold text-primary">{training.our_services}</h5>
                  <p className="text-muted mb-2">Professional training services</p>
                  <div className="price-tag bg-light rounded-pill px-3 py-1 mb-3">
                    <span className="fw-bold text-primary">Contact for pricing</span>
                  </div>
                  <Button variant="outline-success" className="px-4">
                    MORE DETAILS
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>

        {/* ================== How We Can Help You Section ================== */}
        <section className="help-section my-5">
          <h4 className="fw-bold text-primary text-center mb-4">
            How We Can Help You
          </h4>

          <Row className="g-4 justify-content-center">
            {/* Our Strength Card */}
            <Col md={6} lg={5}>
              <Card className="help-card shadow-sm border-0 rounded-3 h-100">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="help-icon bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaShieldAlt size={20} />
                    </div>
                    <h5 className="fw-bold mb-0">Our Strength</h5>
                  </div>
                  <div className="strength-badges">
                    <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Supportive</Badge>
                    <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Results Oriented</Badge>
                    <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Professional</Badge>
                    <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Ethical</Badge>
                    <Badge bg="light" text="dark" className="me-2 mb-2 p-2">Flexible</Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* We Speak Card */}
            <Col md={6} lg={5}>
              <Card className="help-card shadow-sm border-0 rounded-3 h-100">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="help-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaGlobe size={20} />
                    </div>
                    <h5 className="fw-bold mb-0">We Speak</h5>
                  </div>
                  <div className="language-badges">
                    <Badge bg="primary" className="me-2 mb-2 p-2 fs-6">
                      English
                    </Badge>
                    <Badge bg="success" className="me-2 mb-2 p-2 fs-6">
                      Cantonese
                    </Badge>
                    <Badge bg="warning" text="dark" className="me-2 mb-2 p-2 fs-6">
                      Mandarin
                    </Badge>
                  </div>
                  <p className="text-muted mt-2 mb-0">Multilingual support for better communication</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>

        {/* ================== Contact Detail Section ================== */}
        <section className="contact-section my-5">
          <h4 className="fw-bold text-primary text-center mb-4">Contact Detail</h4>
          
          <Row className="g-4 justify-content-center">
            {/* Contact Details Card */}
            <Col md={6} lg={5}>
              <Card className="contact-detail-card shadow-sm border-0 rounded-3 h-100">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="contact-main-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaIdCard size={24} />
                    </div>
                    <h5 className="fw-bold mb-0 text-primary">Contact Information</h5>
                  </div>

                  {/* Address */}
                  <div className="contact-item d-flex align-items-center mb-3">
                    <div className="contact-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaMapMarkerAlt size={16} />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">Address</h6>
                      <p className="text-muted mb-0">8 Finance St, Central, Hong Kong</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="contact-item d-flex align-items-center mb-3">
                    <div className="contact-icon bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaPhone size={14} />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">Phone</h6>
                      <p className="text-muted mb-0">+852-95284866</p>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="contact-item d-flex align-items-center mb-3">
                    <div className="contact-icon bg-success text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaWhatsapp size={16} />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">WhatsApp</h6>
                      <p className="text-muted mb-0">+852-95284866</p>
                    </div>
                  </div>

                  {/* Time */}
                  <div className="contact-item d-flex align-items-center mb-3">
                    <div className="contact-icon bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaClock size={14} />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">Time</h6>
                      <p className="text-muted mb-0">09:00 AM to 06:00 PM</p>
                    </div>
                  </div>

                  {/* Licence */}
                  <div className="contact-item d-flex align-items-center mb-3">
                    <div className="contact-icon bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaIdCard size={14} />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">Licence</h6>
                      <p className="text-muted mb-0">N/A</p>
                    </div>
                  </div>

                  {/* Day Off */}
                  <div className="contact-item d-flex align-items-center">
                    <div className="contact-icon bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaCalendarAlt size={14} />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-1">Day Off</h6>
                      <p className="text-muted mb-0">N/A</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Submit Review Card */}
            <Col md={6} lg={5}>
              <Card className="review-card shadow-sm border-0 rounded-3 h-100">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="review-main-icon bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center me-3">
                      <FaStar size={24} />
                    </div>
                    <h5 className="fw-bold mb-0 text-primary">Submit Your Review</h5>
                  </div>

                  <div className="review-input mb-4">
                    <textarea 
                      className="form-control" 
                      placeholder="Write Your review here"
                      rows="6"
                      style={{ 
                        borderRadius: '0px', 
                        border: '1px solid #dee2e6',
                        resize: 'vertical',
                        minHeight: '120px'
                      }}
                    ></textarea>
                  </div>

                  <Button variant="primary" className="w-100 py-2 fw-semibold mb-3">
                    <FaStar className="me-2" />
                    SUBMIT REVIEW
                  </Button>

                  <div className="text-center">
                    <small className="text-muted">Pennant Miss Araman</small>
                  </div>

                  {/* Report this Training */}
                  <div className="report-section text-center mt-4 pt-3 border-top">
                    <Button variant="outline-danger" className="px-4 py-2">
                      <FaExclamationTriangle className="me-2" />
                      Report this Training
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </section>
      </div>
    </>
  );
};

export default IndividualTraining;
